import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AppNav from '../components/AppNav';
import CompleteProfileNudge from '../components/CompleteProfileNudge';
import PicksCarousel from '../components/PicksCarousel';
import AIMatchOfDay from '../components/AIMatchOfDay';
import { apiClient } from '../api';
import { MapPin, Heart, User, SlidersHorizontal, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../components/ui/popover';
import { useOnlineUsers } from '../hooks/useOnlineUsers';
import { INTEREST_SUGGESTIONS } from '../constants';
import BoostButton from '../components/BoostButton';
import EmailVerifyBanner from '../components/EmailVerifyBanner';
import SocialProofBanner from '../components/SocialProofBanner';
import EmptyBrowseInvite from '../components/EmptyBrowseInvite';
import FoundingMemberBadge from '../components/FoundingMemberBadge';

const GENDER_LABELS = { female: 'Women', male: 'Men', other: 'Other' };

const Browse = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gender, setGender] = useState('all');
  const [ageRange, setAgeRange] = useState([18, 60]);
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [location, setLocation] = useState('');
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [hasPhotos, setHasPhotos] = useState(false);
  // `maxDistance === null` means "no distance cap"; the slider has an "Any"
  // sentinel state (slider value 0 -> null) so users can opt OUT.
  const [maxDistance, setMaxDistance] = useState(null);
  const { online } = useOnlineUsers();

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const params = { min_age: ageRange[0], max_age: ageRange[1] };
      if (gender !== 'all') params.gender = gender;
      if (selectedInterests.length > 0) params.interests = selectedInterests.join(',');
      if (location.trim()) params.location = location.trim();
      if (onlineOnly) params.online_only = true;
      if (hasPhotos) params.has_photos = true;
      if (maxDistance !== null && maxDistance > 0) params.max_distance_km = maxDistance;
      const res = await apiClient.get('/profiles', { params });
      setProfiles(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [gender, ageRange, selectedInterests, location, onlineOnly, hasPhotos, maxDistance]);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

  const toggleInterest = (i) => {
    setSelectedInterests((prev) =>
      prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]
    );
  };

  const clearFilters = () => {
    setGender('all');
    setAgeRange([18, 60]);
    setSelectedInterests([]);
    setLocation('');
    setOnlineOnly(false);
    setHasPhotos(false);
    setMaxDistance(null);
  };

  const activeFilterCount =
    (gender !== 'all' ? 1 : 0) +
    (selectedInterests.length > 0 ? 1 : 0) +
    (location.trim() ? 1 : 0) +
    (onlineOnly ? 1 : 0) +
    (hasPhotos ? 1 : 0) +
    (maxDistance !== null && maxDistance > 0 ? 1 : 0);

  let content;
  if (loading) {
    content = (
      <div className="flex justify-center py-16" data-testid="browse-loading">
        <div className="w-10 h-10 border-4 border-fnl-pink border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  } else if (profiles.length === 0) {
    content = (
      <EmptyBrowseInvite
        hasFilters={activeFilterCount > 0}
        onClearFilters={clearFilters}
      />
    );
  } else {
    content = (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="browse-grid">
        {profiles.map((p) => (
          <Link
            key={p.user_id}
            to={`/profile/${p.user_id}`}
            className="group bg-fnl-purple/60 rounded-2xl overflow-hidden border border-purple-900/40 card-hover"
            data-testid={`browse-card-${p.user_id}`}
          >
            <div className="aspect-[3/4] relative bg-fnl-purple-light overflow-hidden">
              {p.photo_base64 ? (
                <img src={p.photo_base64} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User className="w-20 h-20 text-fnl-pink/50" />
                </div>
              )}
              {online.has(p.user_id) && (
                <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-black/55 backdrop-blur-sm px-2 py-1 rounded-full">
                  <span className="w-2 h-2 rounded-full bg-green-400 shadow-[0_0_8px_rgba(74,222,128,0.9)] animate-pulse"></span>
                  <span className="text-white text-[10px] font-semibold uppercase tracking-wider">Online</span>
                </div>
              )}
              {p.distance_km != null && (
                <div className="absolute top-3 left-3 inline-flex items-center gap-1 bg-fnl-pink/85 backdrop-blur-sm px-2 py-1 rounded-full shadow-md" data-testid={`distance-pill-${p.user_id}`}>
                  <MapPin className="w-3 h-3 text-white" />
                  <span className="text-white text-[10px] font-bold uppercase tracking-wider">
                    {p.distance_km < 1 ? '< 1 km' : `${Math.round(p.distance_km)} km`}
                  </span>
                </div>
              )}
              {p.founding_member && (
                <div className="absolute bottom-3 right-3 z-10">
                  <FoundingMemberBadge size="sm" />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-fnl-dark via-transparent to-transparent" />
              <div className="absolute bottom-3 left-3 right-3 text-white">
                <div className="font-bold text-lg leading-tight">{p.name}, {p.age}</div>
                {p.location && (
                  <div className="text-xs text-purple-200 flex items-center gap-1 mt-0.5">
                    <MapPin className="w-3 h-3" />{p.location}
                  </div>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-fnl-dark">
      <AppNav />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-8">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-white">Browse Members</h1>
            <p className="text-purple-200 mt-1">Find your perfect local match</p>
          </div>
          <div className="flex items-center gap-2">
            <BoostButton />
            <button
              type="button"
              onClick={() => setOnlineOnly((v) => !v)}
              className={
                onlineOnly
                  ? 'inline-flex items-center gap-1.5 rounded-full pl-2 pr-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-green-500/25 border border-green-400/60 text-green-200'
                  : 'inline-flex items-center gap-1.5 rounded-full pl-2 pr-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-fnl-purple-light/40 border border-purple-700 text-purple-200 hover:text-white'
              }
              data-testid="browse-live-now-pill"
            >
              <span className="relative flex items-center justify-center w-2.5 h-2.5">
                <span className={onlineOnly ? "absolute inset-0 rounded-full bg-green-400 animate-ping opacity-75" : "hidden"}></span>
                <span className="relative w-2.5 h-2.5 rounded-full bg-green-400 shadow-[0_0_8px_rgba(74,222,128,0.9)]" />
              </span>
              Live now
            </button>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="bg-fnl-purple-light/60 border-purple-700 text-white hover:bg-fnl-purple-light hover:text-white relative"
                data-testid="browse-filters-trigger"
              >
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="ml-2 bg-fnl-pink text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full" data-testid="browse-filters-count">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="w-[360px] bg-fnl-purple border-purple-900 text-white p-5"
              data-testid="browse-filters-popover"
            >
              <div className="space-y-5">
                <div>
                  <label className="text-purple-200 text-xs uppercase tracking-wider font-semibold mb-2 block">Looking for</label>
                  <Select value={gender} onValueChange={setGender}>
                    <SelectTrigger className="bg-fnl-purple-light/40 border-purple-700 h-10 text-white" data-testid="filter-gender">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-fnl-purple border-purple-900 text-white">
                      <SelectItem value="all">Everyone</SelectItem>
                      <SelectItem value="female">Women</SelectItem>
                      <SelectItem value="male">Men</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-purple-200 text-xs uppercase tracking-wider font-semibold mb-2 block">
                    Age: {ageRange[0]} — {ageRange[1]}
                  </label>
                  <Slider
                    value={ageRange}
                    onValueChange={setAgeRange}
                    min={18}
                    max={80}
                    step={1}
                    className="mt-3"
                    data-testid="filter-age"
                  />
                </div>

                <div>
                  <label className="text-purple-200 text-xs uppercase tracking-wider font-semibold mb-2 block">Location</label>
                  <Input
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="e.g. Brooklyn, San Diego"
                    className="bg-fnl-purple-light/40 border-purple-700 h-10 text-white placeholder:text-purple-400"
                    data-testid="filter-location"
                  />
                </div>

                <div data-testid="filter-distance-row">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-purple-200 text-xs uppercase tracking-wider font-semibold">Max distance</label>
                    <span className="text-fnl-pink text-xs font-bold" data-testid="filter-distance-label">
                      {maxDistance === null || maxDistance === 0 ? 'Any' : `${maxDistance} km`}
                    </span>
                  </div>
                  <Slider
                    value={[maxDistance ?? 0]}
                    onValueChange={(v) => setMaxDistance(v[0] === 0 ? null : v[0])}
                    min={0}
                    max={500}
                    step={5}
                    className="my-1"
                    data-testid="filter-distance-slider"
                  />
                  <div className="flex justify-between text-[10px] text-purple-400 mt-1">
                    <span>Any</span>
                    <span>500 km</span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white text-sm font-semibold">Online now</div>
                    <div className="text-purple-300 text-xs">Only show members currently active</div>
                  </div>
                  <Switch checked={onlineOnly} onCheckedChange={setOnlineOnly} data-testid="filter-online" />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white text-sm font-semibold">Has photos</div>
                    <div className="text-purple-300 text-xs">Hide profiles without gallery photos</div>
                  </div>
                  <Switch checked={hasPhotos} onCheckedChange={setHasPhotos} data-testid="filter-photos" />
                </div>

                {activeFilterCount > 0 && (
                  <Button
                    onClick={clearFilters}
                    variant="ghost"
                    className="w-full text-fnl-pink hover:bg-fnl-pink/10 hover:text-fnl-pink"
                    data-testid="filter-clear"
                  >
                    Clear all filters
                  </Button>
                )}
              </div>
            </PopoverContent>
          </Popover>
          </div>
        </div>

        <CompleteProfileNudge context="browse" />
        <EmailVerifyBanner />
        <SocialProofBanner />
        {activeFilterCount === 0 && <AIMatchOfDay />}
        {activeFilterCount === 0 && <PicksCarousel />}

        {/* Active filter chip strip */}
        {activeFilterCount > 0 && (
          <div className="flex flex-wrap gap-2 mb-6" data-testid="browse-active-filters">
            {gender !== 'all' && (
              <span className="px-3 py-1 rounded-full bg-fnl-pink/20 text-fnl-pink text-xs font-semibold border border-fnl-pink/40">
                {GENDER_LABELS[gender] || 'Other'}
              </span>
            )}
            {selectedInterests.map((i) => (
              <span key={i} className="px-3 py-1 rounded-full bg-fnl-pink/20 text-fnl-pink text-xs font-semibold border border-fnl-pink/40">
                {i}
              </span>
            ))}
            {location.trim() && (
              <span className="px-3 py-1 rounded-full bg-fnl-pink/20 text-fnl-pink text-xs font-semibold border border-fnl-pink/40">
                📍 {location.trim()}
              </span>
            )}
            {hasPhotos && (
              <span className="px-3 py-1 rounded-full bg-fnl-pink/20 text-fnl-pink text-xs font-semibold border border-fnl-pink/40">
                Has photos
              </span>
            )}
            {maxDistance !== null && maxDistance > 0 && (
              <span className="px-3 py-1 rounded-full bg-fnl-pink/20 text-fnl-pink text-xs font-semibold border border-fnl-pink/40" data-testid="active-distance-chip">
                Within {maxDistance} km
              </span>
            )}
          </div>
        )}

        {content}
      </div>
    </div>
  );
};

export default Browse;
